// src/services/servicesApi.js
// API сервис для управления услугами и ценами

import { API_URL } from '../config';

/**
 * Получение токена из localStorage
 */
const getToken = () => {
  const token = localStorage.getItem('token');
  console.log('getToken:', token ? 'Token found (length: ' + token.length + ')' : 'No token');
  return token;
};

/**
 * Базовые заголовки для запросов
 */
const getHeaders = () => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
  console.log('getHeaders:', headers.Authorization ? 'Auth header present' : 'No auth header');
  return headers;
};

/**
 * Обработка ошибок API
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorText = await response.text();
    console.log('API error response:', response.status, errorText);
    try {
      const error = JSON.parse(errorText);
      const detail = error.detail;
      if (detail && typeof detail === 'object') {
        const apiError = new Error(detail.message || `Ошибка ${response.status}`);
        apiError.detail = detail;
        apiError.status = response.status;
        throw apiError;
      }
      const apiError = new Error(detail || `Ошибка ${response.status}`);
      apiError.status = response.status;
      throw apiError;
    } catch (e) {
      if (e.detail || e.status) {
        throw e;
      }
      throw new Error(errorText || `Ошибка ${response.status}`);
    }
  }
  return response.json();
};

// ============================================
// Группы услуг (Service Groups)
// ============================================

/**
 * Получение списка групп услуг
 * @param {Object} params - Параметры запроса
 * @param {string} params.service_type - Фильтр по типу услуги
 * @param {boolean} params.active_only - Только активные группы
 * @param {string} params.client_type - Тип клиента: b2c или b2b
 */
export const getServiceGroups = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.service_type) queryParams.append('service_type', params.service_type);
  if (params.active_only) queryParams.append('active_only', 'true');
  if (params.client_type) queryParams.append('client_type', params.client_type);

  const response = await fetch(`${API_URL}/api/services/groups?${queryParams}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Получение информации о группе услуг
 * @param {number} groupId - ID группы
 */
export const getServiceGroup = async (groupId) => {
  const response = await fetch(`${API_URL}/api/services/groups/${groupId}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Получение информации о группе услуг с проверкой услуг перед удалением
 * @param {number} groupId - ID группы
 */
export const getServiceGroupWithServices = async (groupId) => {
  const response = await fetch(`${API_URL}/api/services/groups/${groupId}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Создание новой группы услуг
 * @param {Object} groupData - Данные группы
 */
export const createServiceGroup = async (groupData) => {
  const response = await fetch(`${API_URL}/api/services/groups`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(groupData),
  });
  return handleResponse(response);
};

/**
 * Обновление группы услуг
 * @param {number} groupId - ID группы
 * @param {Object} groupData - Данные для обновления
 */
export const updateServiceGroup = async (groupId, groupData) => {
  const response = await fetch(`${API_URL}/api/services/groups/${groupId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(groupData),
  });
  return handleResponse(response);
};

/**
 * Деактивация группы услуг (мягкое удаление)
 * @param {number} groupId - ID группы
 * @returns {Promise<{message: string, services_in_group: Array<{id: number, name: string}>}>}
 */
export const deleteServiceGroup = async (groupId) => {
  const response = await fetch(`${API_URL}/api/services/groups/${groupId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

// ============================================
// Услуги (Services)
// ============================================

/**
 * Получение списка типов услуг
 */
export const getServiceTypes = async () => {
  const response = await fetch(`${API_URL}/api/services/types`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Получение списка единиц измерения
 */
export const getUnits = async () => {
  const response = await fetch(`${API_URL}/api/services/units`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Получение списка услуг
 * @param {Object} params - Параметры запроса
 * @param {string} params.service_type - Фильтр по типу услуги
 * @param {number} params.group_id - Фильтр по группе
 * @param {boolean} params.active_only - Только активные услуги
 * @param {string} params.client_type - Тип клиента: b2c или b2b
 */
export const getServices = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.service_type) queryParams.append('service_type', params.service_type);
  if (params.group_id) queryParams.append('group_id', params.group_id);
  if (params.active_only) queryParams.append('active_only', 'true');
  if (params.client_type) queryParams.append('client_type', params.client_type);
  if (params.price_at) queryParams.append('price_at', params.price_at);

  const response = await fetch(`${API_URL}/api/services?${queryParams}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Получение информации об услуге
 * @param {number} serviceId - ID услуги
 */
export const getService = async (serviceId) => {
  const response = await fetch(`${API_URL}/api/services/${serviceId}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Создание новой услуги
 * @param {Object} serviceData - Данные услуги
 */
export const createService = async (serviceData) => {
  const response = await fetch(`${API_URL}/api/services`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(serviceData),
  });
  return handleResponse(response);
};

/**
 * Обновление услуги
 * @param {number} serviceId - ID услуги
 * @param {Object} serviceData - Данные для обновления
 */
export const updateService = async (serviceId, serviceData) => {
  const response = await fetch(`${API_URL}/api/services/${serviceId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(serviceData),
  });
  return handleResponse(response);
};

/**
 * Деактивация услуги (мягкое удаление)
 * @param {number} serviceId - ID услуги
 */
export const deleteService = async (serviceId) => {
  const response = await fetch(`${API_URL}/api/services/${serviceId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

// ============================================
// Цены (Prices)
// ============================================

/**
 * Получение истории цен для услуги
 * @param {number} serviceId - ID услуги
 */
export const getServicePrices = async (serviceId, clientType) => {
  const queryParams = new URLSearchParams();
  if (clientType) queryParams.append('client_type', clientType);

  const response = await fetch(`${API_URL}/api/services/${serviceId}/prices?${queryParams.toString()}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Получение полного аудита услуги (все изменения)
 * @param {number} serviceId - ID услуги
 */
export const getServiceAudit = async (serviceId) => {
  const response = await fetch(`${API_URL}/api/services/${serviceId}/audit`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Создание новой записи о цене
 * @param {number} serviceId - ID услуги
 * @param {Object} priceData - Данные цены
 * @param {number} priceData.price - Цена
 * @param {string} priceData.unit_type - Тип цены: 'rub' или 'percent'
 * @param {string} priceData.effective_from - Дата начала действия (YYYY-MM-DD)
 * @param {string} priceData.comment - Комментарий
 */
export const createServicePrice = async (serviceId, priceData) => {
  const response = await fetch(`${API_URL}/api/services/${serviceId}/prices`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(priceData),
  });
  return handleResponse(response);
};

/**
 * Отмена запланированного изменения цены услуги
 * @param {number} serviceId - ID услуги
 * @param {string} clientType - Тип клиента: b2c, b2b или all
 */
export const cancelScheduledServicePrice = async (serviceId, clientType) => {
  const queryParams = new URLSearchParams();
  if (clientType) queryParams.append('client_type', clientType);

  const response = await fetch(`${API_URL}/api/services/${serviceId}/prices/scheduled?${queryParams.toString()}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

// ============================================
// Статистика
// ============================================

/**
 * Получение сводной статистики по услугам
 */
export const getServicesSummary = async () => {
  const response = await fetch(`${API_URL}/api/services/stats/summary`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse(response);
};

// ============================================
// Экспорт всех функций для удобного импорта
// ============================================

export default {
  // Группы
  getServiceGroups,
  getServiceGroup,
  createServiceGroup,
  updateServiceGroup,
  deleteServiceGroup,
  // Услуги
  getServiceTypes,
  getUnits,
  getServices,
  getService,
  createService,
  updateService,
  deleteService,
  // Цены
  getServicePrices,
  createServicePrice,
  cancelScheduledServicePrice,
  // Статистика
  getServicesSummary,
  // Аудит
  getServiceAudit,
};
